import * as R from 'ramda'
import { createSelector } from 'reselect'
import { getFromLocalStorage } from 'store/reducers/fromLocalStorage'
import * as RE from 'helpers/ramda-ext'
import { DEPARTURE_CITIES_FILTERS_KEY, DEPARTURE_CITIES_NO_TRANSPORTATION_CODES } from 'constants/filters'
import { hasVisibleDepartureCityPicker } from 'domain/packages'
import {
  CityOption,
  FormattedDepartureCitiesForDropdownValue
} from 'types/typescript'
import {
  getCityName,
  getStockByCity,
  isDepartureCityAvailable,
  sortDepartureCityOptions
} from 'domain/pricerDepartureCity'
import { getAllCitiesOfUniverses } from './allProducts'
import {
  getActivePackage,
  getCurrentSelection,
  getPickersBindings,
  getPickersBindingsStock
} from './package'
import {
  GetAvailableDepartureCities, GetDepartureCityAutoSelect, GetDepartureCityFromLocalStorage, GetDepartureCityFromLS,
  GetHasOnlyOneDepartureCity,
  GetHasVisibleDepartureCityPicker, GetIsDepartureCityFromLSAvailable,
  GetHasWithAndWithoutTransportation,
  GetNbAvailableDepartureCitiesWithStock,
  GetSelectedDepartureCity,
  GetSpecificFormatDepartureCitiesForDropdown, GetUniqDepartureCity
} from './pricerDepartureCity.types'
import { getCities, getFavoriteDepartureCityCode, getSaleFilters } from './sale'
import { getDepartureCitiesFilterFromQuery } from './router'

export const getAvailableDepartureCities: GetAvailableDepartureCities = createSelector(
  getCities,
  getFavoriteDepartureCityCode,
  getPickersBindings,
  getPickersBindingsStock,
  (allCities, favoriteDepartureCity, pickersBindings, pickersBindingsStock) => {
    const cityStock = getStockByCity(pickersBindingsStock)
    const availableDepartureCities = pickersBindings
      ? Object.keys(pickersBindings).map(city => {
        const bestPrice = pickersBindings[city].reduce((acc: number, duration) => {
          /*
            The `undefined` check of the hasPrice is here for retrocompat issue
            it should be removed in the future
          */
          if (acc === null || ((acc) > duration.price && (duration.hasPrice === true || duration.hasPrice === undefined))) {
            acc = duration.price
          }
          return acc
        }, null)

        const uppercasedCityKey = city.toUpperCase()
        return ({
          bestPrice,
          key: uppercasedCityKey,
          label: getCityName(allCities, uppercasedCityKey).label,
          stock: cityStock ? cityStock[uppercasedCityKey] : true
        })
      })
      : []

    return sortDepartureCityOptions(favoriteDepartureCity, availableDepartureCities)
  }
)

export const getHasOnlyOneDepartureCity: GetHasOnlyOneDepartureCity = createSelector(
  getAvailableDepartureCities,
  RE.hasOneElement
)

export const getSpecificFormatDepartureCitiesForDropdown: GetSpecificFormatDepartureCitiesForDropdown = createSelector(
  getAvailableDepartureCities,
  getDepartureCitiesFilterFromQuery,
  (availableDepartureCities, departureCitiesFromFilter) => {
    const initial = {
      filtered: { title: 'your_cities', values: [] as FormattedDepartureCitiesForDropdownValue[] },
      others: { title: 'other_cities', values: [] as FormattedDepartureCitiesForDropdownValue[] }
    }

    return availableDepartureCities.reduce((acc, city: FormattedDepartureCitiesForDropdownValue) => {
      const foundCity = departureCitiesFromFilter.find(cityKey => city.key === cityKey)
      if (foundCity) {
        acc.filtered.values.push(city)
      } else {
        acc.others.values.push(city)
      }
      return acc
    }, initial)
  }
)

export const getNbAvailableDepartureCitiesWithStock: GetNbAvailableDepartureCitiesWithStock = createSelector(
  getAvailableDepartureCities,
  R.pipe(
    R.filter(value => value.stock),
    R.length
  )
)

export const getSelectedDepartureCity: GetSelectedDepartureCity = createSelector(
  getCurrentSelection,
  getAvailableDepartureCities,
  (currentSelection, availableDepartureCities) => {
    if (!currentSelection || !currentSelection.departureCity) { return null }

    return R.find(
      R.propEq('key', currentSelection.departureCity),
      availableDepartureCities
    )
  }
)

export const getHasVisibleDepartureCityPicker: GetHasVisibleDepartureCityPicker = createSelector(
  getActivePackage,
  hasVisibleDepartureCityPicker
)

export const getDepartureCityFromLocalStorage: GetDepartureCityFromLocalStorage = createSelector(
  getFromLocalStorage,
  R.path(['configurator', 'departureCity'])
)

export const getIsDepartureCityFromLSAvailable: GetIsDepartureCityFromLSAvailable = createSelector(
  getAvailableDepartureCities,
  getDepartureCityFromLocalStorage,
  (availableDepartureCities, departureCityFromLS) => (
    departureCityFromLS && isDepartureCityAvailable(departureCityFromLS, availableDepartureCities as CityOption[])
  )
)

export const getDepartureCityFromLS: GetDepartureCityFromLS = createSelector(
  getHasVisibleDepartureCityPicker,
  getSelectedDepartureCity,
  getIsDepartureCityFromLSAvailable,
  getDepartureCityFromLocalStorage,
  (hasVisiblePicker, selectedDepartureCity, isDepartureCityFromLSAvailable, departureCityFromLS) => (
    hasVisiblePicker && !selectedDepartureCity && isDepartureCityFromLSAvailable
      ? departureCityFromLS
      : null
  )
)

export const getUniqDepartureCity: GetUniqDepartureCity = createSelector(
  getHasVisibleDepartureCityPicker,
  getSelectedDepartureCity,
  getAvailableDepartureCities,
  getHasOnlyOneDepartureCity,
  (hasVisiblePicker, selectedDepartureCity, availableDepartureCity, hasOnlyOneDepartureCity) => (
    hasVisiblePicker && !selectedDepartureCity && hasOnlyOneDepartureCity && availableDepartureCity[0].stock
      ? availableDepartureCity[0].key
      : null
  )
)

export const getDepartureCityAutoSelect: GetDepartureCityAutoSelect = createSelector(
  getDepartureCityFromLS,
  getUniqDepartureCity,
  (departureCityFromLS, departureCityAutoSelect) => (
    departureCityFromLS ?? departureCityAutoSelect
  )
)

export const getHasWithAndWithoutTransportation: GetHasWithAndWithoutTransportation = createSelector(
  getAllCitiesOfUniverses,
  getSaleFilters,
  (departureCities, filters) => (
    !!filters[DEPARTURE_CITIES_FILTERS_KEY] && departureCities.some(city => DEPARTURE_CITIES_NO_TRANSPORTATION_CODES.includes(city)) &&
      departureCities.some(city => !DEPARTURE_CITIES_NO_TRANSPORTATION_CODES.includes(city))
  )
)
